/**
 * Shared FormField component for rendering form inputs with labels and errors
 * Supports input, phone, select, and datetime field types
 */
import type { AnyFieldApi } from '@tanstack/react-form';
import { Field, FieldLabelRow, FieldContent, FieldError, FieldDescription } from '@/components/ui/field';
import { useTranslation } from '@hooks/useTranslation';
import { shouldShowFieldError } from '../../Form/fieldErrorVisibility';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem
} from '@/components/ui/select';
import { DateTimePicker } from '@/components/ui/date-time-picker';
import { DateRangePicker } from '@/components/ui/date-range-picker';
import { Switch } from '@/components/ui/switch';
import { FIELD_SURFACE } from '@/components/ui/field-surface';
import { cn } from '@/lib/utils';
import { PhoneInput } from '../../UserManagement/PhoneInput';

export type FieldType = 'input' | 'phone' | 'select' | 'datetime' | 'daterange' | 'switch';

export interface DateRangeFieldValue {
  start: string | null;
  end: string | null;
  noEndDate: boolean;
}

export interface FormFieldConfig {
  name: string;
  label: string;
  type: FieldType;
  placeholder?: string;
  inputType?: string;
  options?: Array<{ value: string; label: string }>;
  selectPlaceholder?: string;
  className?: string;
  gridCols?: number;
  hint?: string;
  minDate?: Date;
  endPlaceholder?: string;
  noEndDateLabel?: string;
  /** §5: marks the field optional with a right-aligned tag. Unmarked = required. */
  optional?: boolean;
}

export interface FormFieldProps {
  field: AnyFieldApi;
  config: FormFieldConfig;
  /**
   * §2 error-timing opt-in. When provided, the error is gated through
   * {@link shouldShowFieldError} (typically `submissionAttempts > 0`). Left
   * undefined, the error renders eagerly as before (backward-compatible).
   */
  submitted?: boolean;
}

const LABEL_CLASSES = 'font-funkis text-sm font-semibold text-grey';

export function FormField({ field, config, submitted }: FormFieldProps) {
  const { t } = useTranslation();
  const renderInput = () => {
    switch (config.type) {
      case 'input':
        return (
          <Input
            type={config.inputType}
            value={field.state.value ?? ''}
            onChange={(e) => {
              const val = e.target.value;
              // Handle number inputs - convert string to number or null
              if (config.inputType === 'number') {
                field.handleChange(val === '' ? null : parseInt(val, 10));
              } else {
                field.handleChange(val);
              }
            }}
            onBlur={field.handleBlur}
            placeholder={config.placeholder}
            className={cn(FIELD_SURFACE, 'h-11 font-funkis text-sm placeholder:text-grey-light', config.className || 'px-5')}
          />
        );
      case 'phone':
        return (
          <PhoneInput
            value={(field.state.value ?? '') as string}
            onChange={field.handleChange}
          />
        );
      case 'select':
        return (
          <Select
            value={(field.state.value ?? '') as string}
            onValueChange={field.handleChange}
          >
            <SelectTrigger aria-label={config.label}>
              <SelectValue placeholder={config.selectPlaceholder} />
            </SelectTrigger>
            <SelectContent>
              {config.options?.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        );
      case 'datetime':
        return (
          <DateTimePicker
            value={field.state.value}
            onChange={field.handleChange}
            placeholder={config.placeholder}
            minDate={config.minDate}
          />
        );
      case 'daterange': {
        const DEFAULT_RANGE: DateRangeFieldValue = { start: null, end: null, noEndDate: false };
        const rangeValue = (field.state.value as DateRangeFieldValue | undefined) ?? DEFAULT_RANGE;
        return (
          <DateRangePicker
            startDate={rangeValue?.start ? new Date(rangeValue.start) : undefined}
            endDate={rangeValue?.end ? new Date(rangeValue.end) : undefined}
            onStartDateChange={(date) =>
              field.handleChange({ ...rangeValue, start: date?.toISOString().split('T')[0] ?? null })
            }
            onEndDateChange={(date) =>
              field.handleChange({ ...rangeValue, end: date?.toISOString().split('T')[0] ?? null })
            }
            noEndDate={rangeValue?.noEndDate ?? false}
            onNoEndDateChange={(checked) =>
              field.handleChange({ ...rangeValue, noEndDate: checked, end: checked ? null : rangeValue?.end ?? null })
            }
            placeholder={config.placeholder}
            endPlaceholder={config.endPlaceholder}
            noEndDateLabel={config.noEndDateLabel}
            minDate={config.minDate}
          />
        );
      }
      case 'switch':
        return (
          <div className="flex items-center gap-3 h-11">
            <Switch
              checked={field.state.value ?? false}
              onCheckedChange={field.handleChange}
            />
            <span className="font-funkis text-sm text-grey">
              {field.state.value ? 'Active' : 'Inactive'}
            </span>
          </div>
        );
      default:
        return null;
    }
  };

  const showError =
    submitted === undefined ? true : shouldShowFieldError(field.state.meta, submitted);
  const hasError = field.state.meta.errors?.length > 0;

  return (
    <Field data-invalid={showError && hasError ? true : undefined}>
      <FieldLabelRow
        label={config.label}
        marker={config.optional ? t('form.optional') : undefined}
        className="mb-3"
        labelClassName={LABEL_CLASSES}
      />
      <FieldContent>
        {renderInput()}
        {config.hint && <FieldDescription>{config.hint}</FieldDescription>}
        {showError && <FieldError errors={field.state.meta.errors} />}
      </FieldContent>
    </Field>
  );
}
